import { DatasetData } from "../model_gen/dataset";
import { GeneralService } from "./GeneralService";

export class DatasetService {
  public static getDatasets(
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetsByIntentGroup(
    intentGroupId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/intent_group/${intentGroupId}/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createDataset(
    payload: DatasetData,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.post(
      "/datasets",
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateDataset(
    datasetId: number,
    payload: any,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.delete(
      `/datasets/${datasetId}`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetRequests(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/requests/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getEligibleDatasetRequests(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string,
    sessionId?: string
  ) {
    const queryParams = sessionId
      ? `?session_id=${encodeURIComponent(sessionId)}`
      : "";
    return GeneralService.get(
      `/datasets/${datasetId}/eligible_requests/${offset}/${limit}${queryParams}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static searchEligibleDatasetRequests(
    datasetId: number,
    query: string,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/eligible_requests/search`,
      JSON.stringify({ query }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getEstimatedDatasetRequestCount(
    intentGroupId: number | null,
    applicationId: number | null,
    intentId: number | null,
    dateStart: number | null,
    dateEnd: number | null,
    ignoredApplicationIds: number[] | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    let url = "/datasets/estimated_request_count?";

    if (intentGroupId) {
      url += `intent_group_id=${intentGroupId}&`;
    }

    if (applicationId) {
      url += `application_id=${applicationId}&`;
    }

    if (intentId) {
      url += `intent_id=${intentId}&`;
    }

    if (dateStart) {
      url += `date_start=${dateStart}&`;
    }

    if (dateEnd) {
      url += `date_end=${dateEnd}&`;
    }
    if (ignoredApplicationIds && ignoredApplicationIds.length > 0) {
      url += `ignored_application_ids=${ignoredApplicationIds.join(",")}&`;
    }

    // Remove trailing '&' if present
    url = url.endsWith("&") ? url.slice(0, -1) : url;

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getIntentGroupRequestCount(
    intentGroupId: number,
    dateStart: number | null,
    dateEnd: number | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/count/intent_group/${intentGroupId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getApplicationIntentRequestCount(
    applicationId: number,
    intentId: number,
    dateStart: number | null,
    dateEnd: number | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/count/application/${applicationId}/intent/${intentId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetRequestDistribution(
    datasetId: string | number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/request_distribution`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateDatasetRequestTags(
    datasetId: string | number,
    requestId: string,
    tags: string[],
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}/request/${requestId}/tags`,
      JSON.stringify({ tags }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getRegexReplacementsApplicableCount(
    datasetId: number,
    pattern: string,
    replacement: string,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/modification/regex_replacements/applicable_count`,
      JSON.stringify({ pattern, replacement }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static applyRegexReplacements(
    datasetId: number,
    regexReplacements: any[],
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/modification/regex_replacements`,
      JSON.stringify({ regex_replacements: regexReplacements }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static cloneDataset(
    datasetId: number,
    payload: any,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: string
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/clone`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
